import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import moment from 'moment';
import CBMediaQuery from '../../shared/CBMediaQuery';
import CBModal from '../../shared/CBModal';
import CBDatePickerInput from '../../shared/CBDatePickerInput';
import CBSelectField from '../../shared/form_components/CBSelectField';
import CBCheckbox from '../../shared/form_components/CBCheckbox';
import { finnishDateFormat, getStyle } from '../../../utils/utils';
import {
  composeConsolidatedInvoice as _composeConsolidatedInvoice,
  fetchComposePreview as _fetchComposePreview,
  clearComposePreview as _clearComposePreview,
} from '../../../actions/chainActions';

function formatPrice(value) {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fi-FI');
}

var holdBadgeStyle = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 600,
  backgroundColor: 'rgba(255, 152, 0, 0.15)',
  color: '#e65100',
  border: '1px solid rgba(255, 152, 0, 0.5)',
  marginLeft: 6,
  whiteSpace: 'nowrap',
};

var creditBadgeStyle = {
  ...holdBadgeStyle,
  backgroundColor: 'rgba(211, 47, 47, 0.1)',
  color: '#c62828',
  border: '1px solid rgba(211, 47, 47, 0.4)',
};

class ComposeInvoiceDialog extends React.Component {
  state = {
    step: 1,
    care_service_id: '',
    period_start: null,
    period_end: null,
    errorMessage: null,
    submitted: false,
    previewRequested: false,
    selectedIds: {},
    showOutside: false,
  };

  componentDidUpdate(prevProps) {
    var prevStatus = prevProps.composeInvoiceStatus;
    var status = this.props.composeInvoiceStatus;
    if (this.state.submitted && prevStatus && prevStatus.loading && status && !status.loading && !status.error) {
      this.props.clearComposePreview();
      this.props.onClose();
    }

    var prevPreview = prevProps.composePreviewStatus;
    var previewStatus = this.props.composePreviewStatus;
    if (this.state.previewRequested && prevPreview && prevPreview.loading &&
        previewStatus && !previewStatus.loading) {
      if (previewStatus.error) {
        this.setState({ previewRequested: false });
      } else if (this.props.composePreview) {
        var preview = this.props.composePreview;
        var selectedIds;
        if (this.state.step === 2) {
          // Refresh after a stale selection: keep only picks that are still
          // eligible in the fresh preview.
          selectedIds = this.intersectSelection(preview, this.state.selectedIds);
        } else {
          selectedIds = this.defaultSelection(preview);
        }
        this.setState({
          step: 2,
          previewRequested: false,
          selectedIds: selectedIds,
          errorMessage: null,
        });
      }
    }
  }

  componentWillUnmount() {
    this.props.clearComposePreview();
  }

  defaultSelection = function (preview) {
    // All non-held in-range invoices checked; held ones must be picked by
    // hand — that friction is the point of the hold.
    var sel = {};
    (preview.in_range || []).forEach(function (row) {
      if (!row.invoicing_hold) {
        sel[row.id] = true;
      }
    });
    return sel;
  };

  intersectSelection = function (preview, oldSelection) {
    var sel = {};
    var rows = (preview.in_range || []).concat(preview.outside_range || []);
    rows.forEach(function (row) {
      if (oldSelection[row.id]) {
        sel[row.id] = true;
      }
    });
    return sel;
  };

  updateFormState = function (changedObject) {
    this.setState({ [changedObject.target]: changedObject.value });
  };

  handleFetchPreview = function () {
    var t = this.props.t;
    var care_service_id = this.state.care_service_id;
    var period_start = this.state.period_start;
    var period_end = this.state.period_end;

    if (!care_service_id || !period_start || !period_end) {
      this.setState({ errorMessage: t('fillRequiredFields') });
      return;
    }

    if (new Date(period_start) > new Date(period_end)) {
      // Single-day periods (start == end) are valid — the backend and the
      // month-end rule both use them.
      this.setState({ errorMessage: t('periodStartBeforeEnd') });
      return;
    }

    var csId = parseInt(care_service_id, 10);
    if (isNaN(csId)) {
      this.setState({ errorMessage: t('fillRequiredFields') });
      return;
    }

    this.setState({ errorMessage: null, previewRequested: true });
    this.props.fetchComposePreview({
      care_service_id: csId,
      period_start: period_start,
      period_end: period_end,
    });
  };

  handleRefreshPreview = function () {
    this.setState({ previewRequested: true });
    this.props.fetchComposePreview({
      care_service_id: parseInt(this.state.care_service_id, 10),
      period_start: this.state.period_start,
      period_end: this.state.period_end,
    });
  };

  handleBack = function () {
    this.setState({ step: 1, selectedIds: {}, showOutside: false });
    this.props.clearComposePreview();
  };

  handleSubmit = function () {
    if (this.state.step === 1) {
      this.handleFetchPreview();
      return;
    }
    var ids = this.getSelectedIdList();
    if (ids.length === 0) {
      return;
    }
    this.setState({ errorMessage: null, submitted: true });
    this.props.composeConsolidatedInvoice({
      care_service_id: parseInt(this.state.care_service_id, 10),
      period_start: this.state.period_start,
      period_end: this.state.period_end,
      purchase_invoice_ids: ids,
    });
  };

  getSelectedIdList = function () {
    var selectedIds = this.state.selectedIds;
    return Object.keys(selectedIds).filter(function (id) {
      return selectedIds[id];
    }).map(Number);
  };

  getSelectedSum = function () {
    var preview = this.props.composePreview;
    if (!preview) return 0;
    var selectedIds = this.state.selectedIds;
    var sum = 0;
    (preview.in_range || []).concat(preview.outside_range || []).forEach(function (row) {
      if (selectedIds[row.id]) {
        sum += Number(row.total_amount) || 0;
      }
    });
    return sum;
  };

  toggleInvoice = function (invoiceId) {
    var selectedIds = { ...this.state.selectedIds };
    if (selectedIds[invoiceId]) {
      delete selectedIds[invoiceId];
    } else {
      selectedIds[invoiceId] = true;
    }
    this.setState({ selectedIds: selectedIds });
  };

  toggleSelectAllInRange = function () {
    // Select-all only touches non-held rows; a held invoice is always an
    // individual decision.
    var preview = this.props.composePreview;
    if (!preview) return;
    var nonHeld = (preview.in_range || []).filter(function (row) {
      return !row.invoicing_hold;
    });
    var selectedIds = { ...this.state.selectedIds };
    var allSelected = nonHeld.length > 0 && nonHeld.every(function (row) {
      return selectedIds[row.id];
    });
    nonHeld.forEach(function (row) {
      if (allSelected) {
        delete selectedIds[row.id];
      } else {
        selectedIds[row.id] = true;
      }
    });
    this.setState({ selectedIds: selectedIds });
  };

  isFormValid = function () {
    return !!this.state.care_service_id && !!this.state.period_start && !!this.state.period_end;
  };

  renderInvoiceRow = function (row) {
    var t = this.props.t;
    var checked = !!this.state.selectedIds[row.id];
    return (
      <div
        key={row.id}
        style={styles.default.invoiceRow}
        onClick={function () { this.toggleInvoice(row.id); }.bind(this)}
      >
        <CBCheckbox checked={checked} color="blue" />
        <span style={styles.default.rowInvoiceNumber}>{row.invoice_number || '-'}</span>
        <span style={styles.default.rowWorkshop}>
          {row.workshop_name || row.supplier_name || '-'}
        </span>
        <span style={styles.default.rowDate}>{formatDate(row.invoice_date)}</span>
        <span style={styles.default.rowAmount}>{formatPrice(row.total_amount)}</span>
        {row.invoicing_hold && (
          <span style={holdBadgeStyle} title={row.invoicing_hold_reason || ''}>
            {t('invoicingHold')}
          </span>
        )}
        {Number(row.total_amount) < 0 && (
          <span style={creditBadgeStyle}>{t('creditNote')}</span>
        )}
      </div>
    );
  };

  renderStepOne = function (screenSize) {
    var t = this.props.t;
    var chainCareServices = this.props.chainCareServices;
    var previewStatus = this.props.composePreviewStatus;

    var customerOptions = [{ value: '', label: t('selectCustomer') }];
    if (chainCareServices && chainCareServices.length > 0) {
      chainCareServices.forEach(function (cs) {
        if (cs.id && cs.is_active !== false) {
          customerOptions.push({
            value: String(cs.id),
            label: cs.organization_name || '-',
          });
        }
      });
    }

    return (
      <div style={getStyle(screenSize, styles, 'bodyContainer')}>
        <CBSelectField
          name="care_service_id"
          value={this.state.care_service_id}
          options={customerOptions}
          onChange={this.updateFormState.bind(this)}
          labelText={t('selectCustomer')}
          labelTextStyle={styles.default.inputLabelText}
          inputStyle={styles.default.selectInput}
        />

        <div style={styles.default.dateRow}>
          <div style={styles.default.dateField}>
            <p
              className="font-titillium-web-semi-bold"
              style={styles.default.dateInputLabel}
            >
              {t('periodStart')}
            </p>
            <div className="datepicker-container-white-bg">
              <CBDatePickerInput
                displayedDate={this.state.period_start ? moment(this.state.period_start) : null}
                type="date"
                dateFormat={finnishDateFormat}
                changeDate={function (value) {
                  this.setState({
                    period_start: value ? moment(value).format('YYYY-MM-DD') : null,
                  });
                }.bind(this)}
              />
            </div>
          </div>
          <div style={styles.default.dateField}>
            <p
              className="font-titillium-web-semi-bold"
              style={styles.default.dateInputLabel}
            >
              {t('periodEnd')}
            </p>
            <div className="datepicker-container-white-bg">
              <CBDatePickerInput
                displayedDate={this.state.period_end ? moment(this.state.period_end) : null}
                type="date"
                dateFormat={finnishDateFormat}
                changeDate={function (value) {
                  this.setState({
                    period_end: value ? moment(value).format('YYYY-MM-DD') : null,
                  });
                }.bind(this)}
              />
            </div>
          </div>
        </div>

        {this.state.errorMessage && (
          <p style={styles.default.errorText}>{this.state.errorMessage}</p>
        )}

        {previewStatus && previewStatus.error && (
          <p style={styles.default.errorText}>
            {(previewStatus.error && previewStatus.error.detail) || t('composeError')}
          </p>
        )}
      </div>
    );
  };

  renderStepTwo = function (screenSize) {
    var t = this.props.t;
    var preview = this.props.composePreview;
    var composeStatus = this.props.composeInvoiceStatus;
    var previewStatus = this.props.composePreviewStatus;
    var selectedIds = this.state.selectedIds;

    if (!preview) {
      return (
        <div style={getStyle(screenSize, styles, 'bodyContainer')}>
          <p style={styles.default.infoText}>{t('noInvoicesInPreview')}</p>
        </div>
      );
    }

    var inRange = preview.in_range || [];
    var outside = preview.outside_range || [];
    var nonHeld = inRange.filter(function (row) { return !row.invoicing_hold; });
    var allSelected = nonHeld.length > 0 && nonHeld.every(function (row) {
      return selectedIds[row.id];
    });
    var selectedCount = this.getSelectedIdList().length;
    var staleSelection = composeStatus && composeStatus.error &&
      composeStatus.error.invalid_ids;

    return (
      <div style={getStyle(screenSize, styles, 'bodyContainer')}>
        <div style={styles.default.stepHeader}>
          <span style={styles.default.stepHeaderText}>
            {formatDate(this.state.period_start)} {'–'} {formatDate(this.state.period_end)}
          </span>
          <a
            style={styles.default.backLink}
            onClick={this.handleBack.bind(this)}
          >
            {t('back')}
          </a>
        </div>

        <div style={styles.default.listContainer}>
          <div style={styles.default.sectionHeader}>
            <div
              style={styles.default.selectAllRow}
              onClick={this.toggleSelectAllInRange.bind(this)}
            >
              <CBCheckbox checked={allSelected} color="blue" />
              <span style={styles.default.sectionTitle}>
                {t('inRangeInvoices')} ({inRange.length})
              </span>
            </div>
          </div>
          {inRange.length > 0 ? (
            inRange.map(this.renderInvoiceRow.bind(this))
          ) : (
            <p style={styles.default.infoText}>{t('noInvoicesInPreview')}</p>
          )}

          {outside.length > 0 && (
            <div style={styles.default.outsideSection}>
              <a
                style={styles.default.showOutsideLink}
                onClick={function () {
                  this.setState({ showOutside: !this.state.showOutside });
                }.bind(this)}
              >
                {t('showOutsideRange')} ({outside.length}{preview.outside_range_truncated ? '+' : ''})
              </a>
              {this.state.showOutside && (
                <div>
                  {outside.map(this.renderInvoiceRow.bind(this))}
                  {preview.outside_range_truncated && (
                    <p style={styles.default.infoText}>{t('outsideRangeTruncated')}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={styles.default.footerRow}>
          <span>{t('selectedCount')}: {selectedCount}</span>
          <span style={{ fontWeight: 700 }}>
            {t('selectedSum')}: {formatPrice(this.getSelectedSum())}
          </span>
        </div>
        <p style={styles.default.sumNote}>{t('selectedSumNote')}</p>

        {staleSelection && (
          <div style={styles.default.staleBox}>
            <p style={styles.default.errorText}>{t('selectionStale')}</p>
            <a
              style={styles.default.backLink}
              onClick={this.handleRefreshPreview.bind(this)}
            >
              {previewStatus && previewStatus.loading ? '...' : t('refreshPreview')}
            </a>
          </div>
        )}
        {composeStatus && composeStatus.error && !staleSelection && (
          <p style={styles.default.errorText}>
            {(composeStatus.error && composeStatus.error.detail) || t('composeError')}
          </p>
        )}
      </div>
    );
  };

  render() {
    var t = this.props.t;
    var composeStatus = this.props.composeInvoiceStatus;
    var previewStatus = this.props.composePreviewStatus;
    var step = this.state.step;

    var isValid;
    if (step === 1) {
      isValid = this.isFormValid() && !(previewStatus && previewStatus.loading);
    } else {
      isValid = this.getSelectedIdList().length > 0 &&
        !(composeStatus && composeStatus.loading);
    }

    return (
      <CBMediaQuery>
        {function (screenSize) {
          return (
            <CBModal
              modalIsOpen
              closeModal={this.props.onClose}
              title={step === 1 ? t('composeInvoice') : t('selectInvoices')}
              submitText={step === 1 ? t('fetchInvoices') : t('create')}
              cancelText={t('cancel')}
              submitForm={this.handleSubmit.bind(this)}
              isValid={isValid}
            >
              {step === 1
                ? this.renderStepOne(screenSize)
                : this.renderStepTwo(screenSize)}
            </CBModal>
          );
        }.bind(this)}
      </CBMediaQuery>
    );
  }
}

var styles = {
  default: {
    bodyContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 32px',
    },
    inputLabelText: {
      color: 'black',
      textAlign: 'center',
    },
    selectInput: {
      backgroundColor: '#ebebeb',
      fontSize: 14,
      color: 'black',
      borderRadius: 100,
      height: 40,
      border: 'none',
      width: '100%',
      paddingLeft: 16,
      paddingRight: 16,
    },
    dateInputLabel: {
      color: 'black',
      textAlign: 'center',
    },
    dateRow: {
      display: 'flex',
      gap: 16,
      marginTop: 8,
    },
    dateField: {
      flex: 1,
    },
    errorText: {
      color: '#e74c3c',
      fontSize: 13,
      marginTop: 8,
    },
    infoText: {
      color: '#666',
      fontSize: 13,
      margin: '8px 0',
    },
    stepHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    stepHeaderText: {
      color: 'black',
      fontSize: 14,
      fontWeight: 600,
    },
    backLink: {
      color: '#0077b0',
      fontSize: 13,
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    listContainer: {
      maxHeight: '45vh',
      overflowY: 'auto',
      border: '1px solid #e0e0e0',
      borderRadius: 8,
      padding: '4px 8px',
    },
    sectionHeader: {
      borderBottom: '1px solid #e0e0e0',
      padding: '6px 0',
      position: 'sticky',
      top: 0,
      backgroundColor: '#fff',
      zIndex: 1,
    },
    selectAllRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
    },
    sectionTitle: {
      color: 'black',
      fontSize: 13,
      fontWeight: 700,
      marginLeft: 8,
    },
    invoiceRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '6px 0',
      borderBottom: '1px solid #f0f0f0',
      cursor: 'pointer',
    },
    rowInvoiceNumber: {
      color: 'black',
      fontSize: 13,
      fontWeight: 600,
      minWidth: 80,
    },
    rowWorkshop: {
      color: '#444',
      fontSize: 13,
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    rowDate: {
      color: '#666',
      fontSize: 13,
      whiteSpace: 'nowrap',
    },
    rowAmount: {
      color: 'black',
      fontSize: 13,
      fontWeight: 600,
      minWidth: 80,
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    outsideSection: {
      marginTop: 12,
      paddingTop: 8,
      borderTop: '1px solid #e0e0e0',
    },
    showOutsideLink: {
      color: '#0077b0',
      fontSize: 13,
      cursor: 'pointer',
      textDecoration: 'underline',
      display: 'inline-block',
      marginBottom: 4,
    },
    footerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      color: 'black',
      fontSize: 14,
      marginTop: 12,
    },
    sumNote: {
      color: '#888',
      fontSize: 11,
      margin: '2px 0 0',
    },
    staleBox: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    },
  },
  small: {
    bodyContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: '16px 16px',
    },
  },
};

function mapStateToProps(state) {
  return {
    composeInvoiceStatus: state.chain.composeInvoiceStatus,
    composePreview: state.chain.composePreview,
    composePreviewStatus: state.chain.composePreviewStatus,
  };
}

export default connect(
  mapStateToProps,
  {
    composeConsolidatedInvoice: _composeConsolidatedInvoice,
    fetchComposePreview: _fetchComposePreview,
    clearComposePreview: _clearComposePreview,
  },
)(translate('ChainInvoicingView')(ComposeInvoiceDialog));
